import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { DocumentRecord, DocumentCreate, DocumentProcessRequest, DocumentState, DocumentUpdate, DocumentWithUploadTargets } from '../../models/Document'

import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetDocumentsRequest extends BasePageRequest {
  searchTerm?: string
  contactIds?: string[]
  createdByUserIds?: string[]
  documentTypeId?: string
  createdAfter?: string
  createdBefore?: string
  fileName?: string
  fileHash?: string
}

export interface GetAllDocumentsRequest extends BaseRequest {
  searchTerm?: string
  contactIds?: string[]
  createdByUserIds?: string[]
  documentTypeId?: string
  createdAfter?: string
  createdBefore?: string
  fileName?: string
  fileHash?: string
}

export interface GetDocumentByIdRequest extends BaseRequest {
  documentId: string
}

export interface GetDocumentsByBatchRequest extends BaseRequest {
  documentBatchId: string
}

export interface EmailAttachmentReference {
  emailId: string
  attachmentId: string
  documentId: string
  state: DocumentState
}

export interface EmailAttachmentKey {
  emailId: string
  attachmentId: string
}

export interface GetEmailAttachmentReferencesRequest extends BaseRequest {
  connector: string
  emailAttachments: EmailAttachmentKey[]
}

export interface CreateDocumentRequest extends BaseRequest {
  payload: DocumentCreate
}

export interface ProcessDocumentsRequest extends BaseRequest {
  payload: DocumentProcessRequest
}

export interface UpdateDocumentRequest extends BaseRequest {
  documentId: string
  payload: DocumentUpdate
}

export interface DeleteDocumentRequest extends BaseRequest {
  documentId: string
}

export interface DeleteDocumentsByBatchRequest extends BaseRequest {
  documentBatchId: string
}

export interface MarkDocumentFileUploadedRequest extends BaseRequest {
  documentId: string
  fileId: string
}

export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Document'],
  endpoints: (builder) => ({
    getDocuments: builder.query<BasePageResponse<DocumentRecord[]>, GetDocumentsRequest>({
      query: (request) => {
        const { accountId, pageRequest, searchTerm, contactIds, createdByUserIds, documentTypeId, createdAfter, createdBefore, fileName, fileHash } = request

        return {
          url: `/accounts/${accountId}/documents`,
          params: {
            page: pageRequest.page,
            size: pageRequest.size,
            ...(searchTerm ? { searchTerm } : {}),
            ...(contactIds?.length ? { contactIds: contactIds.join(',') } : {}),
            ...(createdByUserIds?.length ? { createdByUserIds: createdByUserIds.join(',') } : {}),
            ...(documentTypeId ? { documentTypeId } : {}),
            ...(createdAfter ? { createdAfter } : {}),
            ...(createdBefore ? { createdBefore } : {}),
            ...(fileName ? { fileName } : {}),
            ...(fileHash ? { fileHash } : {}),
          },
        }
      },
      providesTags: ['Document'],
    }),
    getAllDocuments: builder.query<BaseResponse<DocumentRecord[]>, GetAllDocumentsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, searchTerm, contactIds, createdByUserIds, documentTypeId, createdAfter, createdBefore, fileName, fileHash } = request

        return await paginate<DocumentRecord>(
          {
            url: `/accounts/${accountId}/documents`,
            params: {
              ...(searchTerm ? { searchTerm } : {}),
              ...(contactIds?.length ? { contactIds: contactIds.join(',') } : {}),
              ...(createdByUserIds?.length ? { createdByUserIds: createdByUserIds.join(',') } : {}),
              ...(documentTypeId ? { documentTypeId } : {}),
              ...(createdAfter ? { createdAfter } : {}),
              ...(createdBefore ? { createdBefore } : {}),
              ...(fileName ? { fileName } : {}),
              ...(fileHash ? { fileHash } : {}),
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['Document'],
    }),
    getDocumentById: builder.query<DocumentRecord | null, GetDocumentByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/${request.documentId}`,
        validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
      }),
      transformResponse: (response: BaseResponse<DocumentRecord> | undefined) => {
        return response?.data ?? null
      },
    }),
    getDocumentsByBatch: builder.query<DocumentRecord[] | null, GetDocumentsByBatchRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/documents/batches/${request.documentBatchId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: { data: DocumentRecord[] } | undefined) => {
        return response?.data ?? null
      },
    }),
    getEmailAttachmentReferences: builder.query<BaseResponse<EmailAttachmentReference[]>, GetEmailAttachmentReferencesRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/email-attachment-references`,
        method: 'POST',
        body: { connector: request.connector, emailAttachments: request.emailAttachments },
      }),
      providesTags: [{ type: 'Document', id: 'LIST' }],
    }),
    createDocument: builder.mutation<DocumentWithUploadTargets, CreateDocumentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentWithUploadTargets>) => {
        return response.data
      },
    }),
    processDocuments: builder.mutation<DocumentRecord[], ProcessDocumentsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/process`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentRecord[]>) => {
        return response.data
      },
      invalidatesTags: ['Document'],
    }),
    markDocumentFileUploaded: builder.mutation<void, MarkDocumentFileUploadedRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/${request.documentId}/files/${request.fileId}/uploaded`,
        method: 'POST',
      }),
    }),
    updateDocument: builder.mutation<DocumentRecord, UpdateDocumentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/${request.documentId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentRecord>) => {
        return response.data
      },
      invalidatesTags: ['Document'],
    }),
    deleteDocument: builder.mutation<void, DeleteDocumentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/${request.documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Document'],
    }),
    deleteDocumentsByBatch: builder.mutation<void, DeleteDocumentsByBatchRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/batches/${request.documentBatchId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Document'],
    }),
  }),
})

export const {
  useGetDocumentsQuery,
  useLazyGetDocumentsQuery,
  useLazyGetAllDocumentsQuery,
  useGetAllDocumentsQuery,
  useLazyGetDocumentByIdQuery,
  useLazyGetDocumentsByBatchQuery,
  useGetEmailAttachmentReferencesQuery,
  useCreateDocumentMutation,
  useProcessDocumentsMutation,
  useMarkDocumentFileUploadedMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteDocumentsByBatchMutation,
} = documentsApi
